import { atom } from 'nanostores';
import { getStorageValue } from '@/hooks/browser/use-local-storage';

const isLocalEnv = Boolean(import.meta.env.DEV) || import.meta.env.VITE_ENABLE_EXTERNAL_UI_OVERRIDES === 'true';

export const DEV_HELPER_TABS = [
  { value: 'app_import_overrides', text: 'App import overrides', localOnly: true },
  { value: 'feature_flags', text: 'Feature flags', localOnly: true },
  { value: 'account', text: 'Account', localOnly: true },
  { value: 'populate_workspace', text: 'Populate workspace', localOnly: true },
  { value: 'analytics', text: 'Analytics', localOnly: true },
  { value: 'tanstack_devtools', text: 'TanStack', localOnly: true },
  { value: 'ai_prompting', text: 'AI prompting', localOnly: false },
  { value: 'pluma_tokens', text: 'Pluma tokens', localOnly: false },
] as const;

export type DevHelperTab = (typeof DEV_HELPER_TABS)[number]['value'];

const ACTIVE_TAB_STORAGE_KEY = 'dev-helper-active-tab';

function getInitialActiveTab(): DevHelperTab {
  const stored: unknown = getStorageValue(ACTIVE_TAB_STORAGE_KEY, null);
  const match = DEV_HELPER_TABS.find((tab) => tab.value === stored);
  if (match != null) return match.value;
  return isLocalEnv ? 'app_import_overrides' : 'ai_prompting';
}

export const $isDevHelperOpen = atom<boolean>(false);

// Shared (rather than component-local) so the command dialog can jump straight
// to a tab. Persisted under the same key the dev helper always used.
export const $devHelperActiveTab = atom<DevHelperTab>(getInitialActiveTab());

export function setDevHelperActiveTab(tab: DevHelperTab) {
  $devHelperActiveTab.set(tab);
  try {
    window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, JSON.stringify(tab));
  } catch {
    // If localStorage is not available, we can ignore the error
  }
}

export function openDevHelperTab(tab: DevHelperTab) {
  setDevHelperActiveTab(tab);
  $isDevHelperOpen.set(true);
}

export function toggleDevHelper() {
  $isDevHelperOpen.set(!$isDevHelperOpen.get());
}

export function shouldShowDevHelper(): boolean {
  const isStandaloneEnv = __CIO_ENV__ === 'standalone';
  const isManuallyHidden = window.sessionStorage.getItem('hideDevUI') === 'true';
  return (isLocalEnv || isStandaloneEnv) && !isManuallyHidden;
}
